import React, {
  useMemo,
  useState,
  useCallback,
} from "react";

const PeerContext = React.createContext(null);
export const usePeer = () => React.useContext(PeerContext);

export const PeerProvider = (props) => {
  const [remoteStream, setRemoteStream] = useState(null);

  const peer = useMemo(() => {
    const pc = new RTCPeerConnection({
      iceServers: [
        {
          urls: [
            "stun:stun.l.google.com:19302",
            "stun:global.stun.twilio.com:3478",
          ],
        },
        {
          urls: [
            "turn:stun.evan-brass.net",
            "turn:stun.evan-brass.net?transport=tcp",
            "stun:stun.evan-brass.net",
          ],
          username: "guest",
          credential: "password",
        },
      ],
    });

    pc.oniceconnectionstatechange = () => {
      console.log("[PEER] iceConnectionState:", pc.iceConnectionState);
    };

    // Handle remote tracks
    pc.ontrack = (event) => {
      const incomingTrack = event.track;
      console.log("[PEER] ontrack:", incomingTrack.kind, incomingTrack.id);

      let stream = event.streams[0];
      if (!stream) {
        stream = new MediaStream([incomingTrack]);
      }

      setRemoteStream((prev) => {
        if (prev) {
          const alreadyHasTrack = prev
            .getTracks()
            .some((t) => t.id === incomingTrack.id);
          if (!alreadyHasTrack) {
            prev.addTrack(incomingTrack);
          }
          return prev;
        }
        return stream;
      });
    };

    return pc;
  }, []);

  // ICE candidate listener
  const onIceCandidate = useCallback(
    (callback) => {
      peer.onicecandidate = (event) => {
        if (event.candidate) {
          console.log("[PEER] Local ICE candidate:", event.candidate);
          callback(event.candidate);
        }
      };
    },
    [peer]
  );

  // Add local stream (audio + video)
  const sendStream = useCallback(
    (stream) => {
      if (!stream) return;

      console.log(
        "[PEER] sendStream: tracks:",
        stream.getTracks().map((t) => ({
          id: t.id,
          kind: t.kind,
          enabled: t.enabled,
        }))
      );

      stream.getTracks().forEach((track) => {
        const existingSender = peer
          .getSenders()
          .find((s) => s.track && s.track.kind === track.kind);

        if (existingSender) {
          console.log("[PEER] Replacing", track.kind, "track on sender");
          existingSender.replaceTrack(track);
        } else {
          console.log("[PEER] Adding", track.kind, "track");
          peer.addTrack(track, stream);
        }
      });
    },
    [peer]
  );

  // Create offer (caller)
  const createOffer = useCallback(async () => {
    console.log("[PEER] Creating offer...");
    const offer = await peer.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });
    await peer.setLocalDescription(offer);
    console.log("[PEER] Local description set (offer)");
    return offer;
  }, [peer]);

  // Create answer (callee)
  const createAnswer = useCallback(
    async (offer) => {
      if (!offer) throw new Error("Offer required to create answer");

      console.log("[PEER] Setting remote description with offer:", offer);
      await peer.setRemoteDescription(new RTCSessionDescription(offer));

      console.log("[PEER] Creating answer...");
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      console.log("[PEER] Local description set (answer)");

      return answer;
    },
    [peer]
  );

  // Set remote answer (caller)
  const setRemoteAnswer = useCallback(
    async (answer) => {
      if (!answer) return;
      console.log("[PEER] Setting remote description with answer:", answer);
      await peer.setRemoteDescription(new RTCSessionDescription(answer));
    },
    [peer]
  );

  return (
    <PeerContext.Provider
      value={{
        peer,
        createOffer,
        createAnswer,
        setRemoteAnswer,
        sendStream,
        remoteStream,
        onIceCandidate,
      }}
    >
      {props.children}
    </PeerContext.Provider>
  );
};
